"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { Droplets, ShieldCheck, ArrowRight, CircleCheck as CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON, type L6 } from "@/lib/commonCopy";
interface TechHighlight {
  label: L6;
  val: string;
  desc: L6;
}
const technicalHighlights: TechHighlight[] = [{
  label: {
    en: "Aperture Range",
    zh: "孔径公差范围",
    es: "Rango de abertura",
    de: "Öffnungsbereich",
    fr: "Plage d'ouverture",
    pt: "Faixa de abertura"
  },
  val: "1 µm – 25 mm",
  desc: {
    en: "Woven, sintered & wedge wire",
    zh: "编织、多层烧结与楔形丝",
    es: "Tejida, sinterizada y alambre en cuña",
    de: "Gewebe, Sinter & Spaltsieb",
    fr: "Tissée, frittée et fil profilé",
    pt: "Tecida, sinterizada e arame cunha"
  }
}, {
  label: {
    en: "Max Collapse ΔP",
    zh: "耐压差失稳上限",
    es: "ΔP máx. de colapso",
    de: "Max. Kollaps-ΔP",
    fr: "ΔP max. d'écrasement",
    pt: "ΔP máx. de colapso"
  },
  val: "Up to 350 bar",
  desc: {
    en: "Heavy-duty structural integrity",
    zh: "重载承压抗失稳结构",
    es: "Integridad estructural para servicio pesado",
    de: "Robuste strukturelle Festigkeit",
    fr: "Intégrité structurale pour service sévère",
    pt: "Integridade estrutural para serviço pesado"
  }
}, {
  label: {
    en: "Alloy Grades",
    zh: "油气耐蚀合金",
    es: "Grados de aleación",
    de: "Legierungsgüten",
    fr: "Nuances d'alliages",
    pt: "Graus de liga"
  },
  val: "316L / Duplex / C-276",
  desc: {
    en: "NACE MR0175 sour service",
    zh: "符合 NACE 硫化应力抗腐标准",
    es: "Servicio amargo NACE MR0175",
    de: "NACE MR0175 Sauergasbetrieb",
    fr: "Service acide NACE MR0175",
    pt: "Serviço ácido NACE MR0175"
  }
}, {
  label: {
    en: "Separation Efficiency",
    zh: "多相分离效率",
    es: "Eficiencia de separación",
    de: "Trennleistung",
    fr: "Efficacité de séparation",
    pt: "Eficiência de separação"
  },
  val: "99.9% Knockout",
  desc: {
    en: "Liquid-mist & sand retention",
    zh: "高效液滴除雾与防砂截留",
    es: "Retención de niebla líquida y arena",
    de: "Flüssigkeitsnebel- & Sandabscheidung",
    fr: "Rétention de brouillard liquide et sable",
    pt: "Retenção de névoa líquida e areia"
  }
}];
const copy = {
  eyebrow: {
    en: "Oil & Gas Separation",
    zh: "油气分离",
    es: "Separación de petróleo y gas",
    de: "Öl- und Gastrennung",
    fr: "Séparation pétrole et gaz",
    pt: "Separação de óleo e gás"
  },
  h1: {
    en: "High-Performance Mesh & Screens for Oil & Gas Separation",
    zh: "油气开采与炼化高效分离筛网",
    es: "Mallas y cribas de alto rendimiento para separación de petróleo y gas",
    de: "Hochleistungs-Drahtgewebe und Siebe für die Öl- und Gastrennung",
    fr: "Toiles métalliques et tamis haute performance pour séparation pétrole et gaz",
    pt: "Telas e peneiras de alto desempenho para separação de óleo e gás"
  },
  lead: {
    en: "Engineered stainless steel woven wire mesh, sintered laminates, and wedge wire screen assemblies built for downhole sand control, gas scrubber mist elimination, and produced water separation. Providing aperture ratings from 1 µm to 25 mm in 316L, 2205/2507 Duplex, and Hastelloy C-276 with high differential collapse strength and NACE MR0175 sour service compliance.",
    zh: "专为井口防砂、气液除沫、采出水深度处理及催化剂床层支承设计的耐蚀不锈钢编织丝网、烧结多孔板与楔形丝滤筒。孔径覆盖 1 µm 至 25 mm，适配 316L、超级双相钢及哈氏合金等工况材质，提供卓越的耐高压差冲击与抗硫化氢腐蚀性能。",
    es: "Mallas tejidas de acero inoxidable, laminados sinterizados y conjuntos de tamiz de alambre en cuña diseñados para control de arena en fondo de pozo, eliminación de niebla en scrubbers y separación de agua de producción. Rango de abertura de 1 µm a 25 mm en 316L, Duplex 2205/2507 y Hastelloy C-276 con alta resistencia al colapso y conformidad NACE MR0175.",
    de: "Präzisions-Drahtgewebe aus Edelstahl, Sinterverbundelemente und Spaltsiebbaugruppen für Bohrloch-Sandschutz, Tropfenabscheidung in Gaswäschern und die Aufbereitung von Lagerstättenwasser. Porenweiten von 1 µm bis 25 mm in 316L, Duplex 2205/2507 und Hastelloy C-276 mit hoher Kollapsdruckfestigkeit und NACE MR0175-Konformität.",
    fr: "Toiles métalliques tissées en acier inoxydable, stratifiés frittés et crépines à fil profilé pour le contrôle du sable en fond de puits, l'élimination des brouillards et la séparation des eaux de production. Seuils de 1 µm à 25 mm en 316L, Duplex 2205/2507 et Hastelloy C-276 avec haute résistance à l'écrasement et conformité NACE MR0175.",
    pt: "Telas tecidas em aço inoxidável, laminados sinterizados e conjuntos de arame em cunha desenvolvidos para controle de areia em poços, desembaçamento de gás e separação de água produzida. Aberturas de 1 µm a 25 mm em 316L, Duplex 2205/2507 e Hastelloy C-276 com alta resistência ao colapso e conformidade NACE MR0175."
  },
  ctaSecondary: {
    en: "Sintered Multilayer Mesh",
    zh: "烧结多层网技术",
    es: "Malla sinterizada multicapa",
    de: "Mehrlagiges Sintergewebe",
    fr: "Toile multicouche frittée",
    pt: "Malha sinterizada multicamada"
  },
  imgAlt: {
    en: "Stainless steel multi-layer mesh screen for solid-liquid filtration",
    zh: "油气固液相分离多层不锈钢丝网滤板",
    es: "Panel filtrante multicapa de acero inoxidable para separación sólido-líquido",
    de: "Mehrlagige Edelstahl-Filterplatte für die Fest-Flüssig-Filtration",
    fr: "Panneau filtrant multicouche en acier inoxydable pour filtration solide-liquide",
    pt: "Painel filtrante multicamada de aço inoxidável para filtração sólido-líquido"
  },
  badgeVisual: {
    en: "Heavy-Duty Multi-Phase Separation",
    zh: "重载固液与气液多相分离",
    es: "Separación multifásica de servicio pesado",
    de: "Schwere Mehrphasentrennung",
    fr: "Séparation polyphasique sévère",
    pt: "Separação multifásica para serviço pesado"
  },
  cardTitle: {
    en: "316L / Duplex / C-276 Separation Elements",
    zh: "316L / 双相钢 / C-276 油气分离滤材",
    es: "Elementos de separación en 316L / Duplex / C-276",
    de: "Trennelemente aus 316L / Duplex / C-276",
    fr: "Éléments de séparation en 316L / Duplex / C-276",
    pt: "Elementos de separação em 316L / Duplex / C-276"
  },
  cardSubtitle: {
    en: "Rigid high-pressure screen assemblies for wellhead and scrubber vessels",
    zh: "适用于井口防砂、高压分离器与酸性气体除沫器的高刚性滤材",
    es: "Conjuntos de cribas de alta presión para cabezales de pozo y depuradores",
    de: "Formstabile Hochdruck-Siebbaugruppen für Bohrlochkopf- und Wäscherbehälter",
    fr: "Ensembles filtrants haute pression rigides pour têtes de puits et laveurs",
    pt: "Conjuntos de telas de alta pressão rígidos para cabeças de poço e depuradores"
  },
  cardFooterLeft: {
    en: "Screen tubes, demisters & custom packs",
    zh: "丝网滤筒、除沫丝网垫与定制滤芯",
    es: "Tubos filtrantes, eliminadores de niebla y paquetes a medida",
    de: "Filterrohre, Demister & maßgefertigte Pakete",
    fr: "Tubes filtrants, dévésiculeurs et packs sur mesure",
    pt: "Tubos filtrantes, eliminadores de névoa e pacotes sob medida"
  },
  cardFooterRight: {
    en: "Screen Tubes",
    zh: "筛管产品",
    es: "Tubos de criba",
    de: "Siebrohre",
    fr: "Tubes de tamis",
    pt: "Tubos de peneira"
  }
};
export default function Separation() {
  const {
    lang
  } = useLanguage();
  return <section id="separation" data-nav="light" className="bg-background text-foreground mf-hero border-b border-border">
      <div className="mf-container">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-8">
          {/* Left Column (7 cols on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start text-left">
            {/* Eyebrow Badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 mf-caption font-mono tracking-tight text-foreground">
                <Droplets className="size-3.5 text-primary" />
                <span>{pick(lang, copy.eyebrow)}</span>
              </Badge>
              <Badge variant="outline" className="bg-muted text-foreground rounded-md border border-border px-2.5 py-1 mf-caption font-mono">
                <ShieldCheck className="mr-1 size-3 text-primary inline" />
                ISO 9044 / ASTM E2016
              </Badge>
            </div>

            {/* Main Headline */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {pick(lang, copy.h1)}
            </h1>

            {/* Supporting Paragraph */}
            <p className="mt-4 max-w-2xl mf-lead text-muted-foreground">
              {pick(lang, copy.lead)}
            </p>

            {/* 2 CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="px-6 text-sm font-semibold transition-colors w-full sm:w-auto">
                <Link to="/RequestAQuotePage" className="inline-flex items-center justify-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="px-6 text-sm font-semibold transition-colors w-full sm:w-auto">
                <Link to="/SinteredMultilayerMeshPage" className="inline-flex items-center justify-center gap-2">
                  <span>{pick(lang, copy.ctaSecondary)}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Visual Spec Panel (5 cols on lg) */}
          <Reveal delay={50} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card text-card-foreground gap-0 py-0">
              {/* Product Visual Container */}
              <div className="mf-card-media mf-ratio-hero relative w-full">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-81fe57.png" alt={pick(lang, copy.imgAlt)} loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute top-3 left-3">
                  <Badge className="bg-card text-foreground border border-border px-2.5 py-1 mf-caption font-semibold rounded-md flex items-center gap-1.5">
                    <CheckCircle2 className="size-3.5 text-primary" />
                    <span>{pick(lang, copy.badgeVisual)}</span>
                  </Badge>
                </div>
              </div>

              {/* Single Footer Row */}
              <CardContent className="p-4 bg-card flex items-center justify-between mf-caption">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                  <span>{pick(lang, copy.cardFooterLeft)}</span>
                </div>
                <Link to="/ScreenTubesPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                  <span>{pick(lang, copy.cardFooterRight)}</span>
                  <ArrowRight className="size-3" />
                </Link>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip below grid */}
        <div className="mf-spec-strip">
          {technicalHighlights.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">
                {pick(lang, spec.label)}
              </span>
              <span className="mf-spec-strip__value">
                {spec.val}
              </span>
            </div>)}
        </div>
      </div>
    </section>;
}